import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { toast } from "react-toastify";

import {
  initialState,
  handleFulfilled,
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper,
  resetNestedParamsHelper,
  setSortParamsHelper,
  setFilterTagHelper,
  resetFilterTagHelper,
  handlePayload
} from "helpers/reducerHelpers";

const requestUserAccountUrl = "/request/user_accounts";

export const getRequestUserAccounts = createAsyncThunk(
  "UserAccount/getRequestUserAccounts",
  async (params) => {
    try {
      const response = await apiAxios.get(requestUserAccountUrl, { params } )
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const getRequestUserAccount = createAsyncThunk(
  "UserAccount/getRequestUserAccount",
  async (recordId) => {
    try {
      const response = await apiAxios.get(`${requestUserAccountUrl}/${recordId}`)
      return response.data
    }
    catch(error) {
      throw new Error(error.message)
    }
  }
)

export const createRequestUserAccount = createAsyncThunk(
  "UserAccount/createRequestUserAccount",
  async (data, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(requestUserAccountUrl, data)
      return response.data
    }
    catch(error){
      return rejectWithValue(error.response.data)
    }
  }
)

export const updateRequestUserAccount = createAsyncThunk(
  "UserAccount/updateRequestUserAccount",
  async ({recordId, data}, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${requestUserAccountUrl}/${recordId}`, data)
      return response.data
    }
    catch(error){
      return rejectWithValue(error.response.data)
    }
  }
)

export const deleteRequestUserAccount = createAsyncThunk(
  "UserAccount/deleteRequestUserAccount",
  async (recordId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.delete(`${requestUserAccountUrl}/${recordId}`)
      return response.data
    }
    catch(error){
      return rejectWithValue(error.response.data)
    }
  }
)

export const closeRequestUserAccount = createAsyncThunk(
  "UserAccount/closeRequestUserAccount",
  async ({ recordId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${requestUserAccountUrl}/${recordId}/close`, data)
      return response.data
    }
    catch(error){
      return rejectWithValue(error.response.data)
    }
  }
)

export const processRequestUserAccount = createAsyncThunk(
  "UserAccount/processRequestUserAccount",
  async ({ recordId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${requestUserAccountUrl}/${recordId}/hold_to_process`, data)
      return response.data
    }
    catch(error){
      return rejectWithValue(error.response.data)
    }
  }
)

export const getEmailRecipients = createAsyncThunk(
  "UserAccount/getEmailRecipients",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${requestUserAccountUrl}/email_recipients`)
      return response.data
    }
    catch(error){
      return rejectWithValue(error.response.data)
    }
  }
)

export const reSendEmailUserAccount = createAsyncThunk(
  "UserAccount/reSendEmailUserAccount",
  async (recordId, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${requestUserAccountUrl}/${recordId}/resend_email`)
      return response.data
    }
    catch(error){
      return rejectWithValue(error.response.data)
    }
  }
)

export const exportData = createAsyncThunk(
  "UserAccount/exportData",
  async (params) => {
    //remove page and per_page in object
    const { page, per_page, ...rest } = params
    console.log(rest, page, per_page)

    const response = await apiAxios.get(`${requestUserAccountUrl}/export`, {params, responseType: 'blob'})
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `request-user-account.xlsx`);
    document.body.appendChild(link);
    link.click();
  }
)

const isRejectedAction = (action) => (action.type.startsWith("UserAccount/")) && action.type.endsWith("/rejected");

const userAccountSlice = createSlice({
  name: "request_user_account",
  initialState: {
    ...initialState,
  },
  reducers: {
    setNestedPage: setNestedPageHelper,
    setNestedPageSize: setNestedPageSizeHelper,
    setNestedParams: setNestedParamsHelper,
    resetNestedParams: resetNestedParamsHelper,
    setSortParams: setSortParamsHelper,
    setFilterTag: setFilterTagHelper,
    resetFilterTag: resetFilterTagHelper
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRequestUserAccounts.fulfilled, (state, action) => {
        handleFulfilled(state, action, "allData");
        // Store statistics separately
        if (action.payload?.statistics) {
          state.allData.statistics = action.payload.statistics;
        }
      })
      .addCase(getRequestUserAccount.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(createRequestUserAccount.fulfilled, (state, action) => {
        handlePayload(state, action, "create")
      })
      .addCase(updateRequestUserAccount.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(deleteRequestUserAccount.fulfilled, (state, action) => {
        handlePayload(state, action, "delete")
      })
      .addCase(closeRequestUserAccount.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(processRequestUserAccount.fulfilled, (state, action) => {
        handlePayload(state, action)
      })
      .addCase(exportData.pending, (state) => {
        state.exporting = true
      })
      .addCase(exportData.fulfilled, (state) => {
        state.exporting = false
        toast.success("Data has been exported successfully.")
      })
      .addMatcher(
        isRejectedAction,
        (state, action) => {
          state.loading = false;
          toast.error(action.payload?.message || action.payload?.error || action?.error?.message)
        }
      );
  }
})

export const {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  resetNestedParams,
  setSortParams,
  setFilterTag,
  resetFilterTag
} = userAccountSlice.actions;

export default userAccountSlice.reducer;